import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Flame, ArrowRight, HandHeart, UserPlus, UserCheck, Users } from "lucide-react";

function initial(name) {
    return (name || "P").trim().charAt(0).toUpperCase();
}

function formatTime(t) {
    if (!t) return "";
    const [hh, mm] = t.split(":");
    const h = parseInt(hh, 10);
    if (Number.isNaN(h)) return t;
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${mm || "00"} ${ampm}`;
}

export default function TopWarriorsSection() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [warriors, setWarriors] = useState([]);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        api.get("/warriors/top", { params: { limit: 6 } })
            .then(({ data }) => setWarriors(data || []))
            .catch(() => setWarriors([]));
    }, [user?.id]);

    const toggleFollow = async (w) => {
        if (!user?.id) { navigate("/login"); return; }
        if (w.id === user.id) return;
        setBusyId(w.id);
        try {
            const next = !w.is_following;
            const { data } = next
                ? await api.post(`/warriors/${w.id}/follow`)
                : await api.delete(`/warriors/${w.id}/follow`);
            setWarriors((arr) => arr.map((x) => x.id === w.id ? { ...x, is_following: data.following, follower_count: data.followers } : x));
        } catch { /* ignore */ }
        finally { setBusyId(null); }
    };

    if (!warriors.length) return null;

    return (
        <section className="mx-auto max-w-7xl px-6 md:px-10 py-16" data-testid="landing-top-warriors">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs tracking-[0.28em] uppercase" style={{ color: "#7F1D1D" }}>{t("topwarriors.kicker")}</div>
                    <h2 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "#1E1B4B" }}>{t("topwarriors.h")}</h2>
                    <p className="mt-3 max-w-2xl" style={{ color: "#525252" }}>{t("topwarriors.sub")}</p>
                </div>
                <Link to="/join-warriors" className="btn-gold inline-flex items-center gap-2 text-sm" data-testid="landing-topwarriors-cta">
                    {t("topwarriors.cta")} <ArrowRight size={16} />
                </Link>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {warriors.map((w, i) => {
                    const isSelf = user?.id === w.id;
                    return (
                        <article key={w.id} className={`p-card p-6 fade-up delay-${(i % 4) + 1}`} data-testid={`landing-warrior-${w.id}`}>
                            <div className="flex items-center gap-4">
                                <div className="h-14 w-14 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0"
                                     style={{ background: "#1E1B4B", color: "#D4AF37", border: "2px solid #D4AF37" }}>
                                    {w.photo_url ? (
                                        <img src={w.photo_url} alt={w.name} className="h-full w-full object-cover" />
                                    ) : (
                                        <span className="font-display text-2xl">{initial(w.name)}</span>
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="font-display text-xl leading-tight" style={{ color: "#1E1B4B" }}>{w.name}</div>
                                    {w.prayer_time && (
                                        <div className="text-xs mt-0.5 flex items-center gap-1" style={{ color: "#7F1D1D" }}>
                                            <Flame size={12} /> {formatTime(w.prayer_time)}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {w.bio ? (
                                <p className="mt-4 text-sm leading-relaxed" style={{ color: "#525252" }}>
                                    “{w.bio}”
                                </p>
                            ) : (
                                <p className="mt-4 text-sm italic leading-relaxed" style={{ color: "#8a8a8a" }}>
                                    A faithful warrior of quiet, steady prayer.
                                </p>
                            )}
                            <div className="mt-5 flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex items-center gap-3 text-xs" style={{ color: "#1E1B4B" }}>
                                    <span className="inline-flex items-center gap-1.5"><HandHeart size={12} style={{ color: "#D4AF37" }} /> {w.prayer_count || 0} {t("topwarriors.prayers")}</span>
                                    <span className="inline-flex items-center gap-1.5" data-testid={`landing-warrior-followers-${w.id}`}>
                                        <Users size={12} style={{ color: "#7F1D1D" }} /> {w.follower_count || 0} follower{(w.follower_count || 0) === 1 ? "" : "s"}
                                    </span>
                                </div>
                                {!isSelf && (
                                    <button
                                        onClick={() => toggleFollow(w)}
                                        disabled={busyId === w.id}
                                        className="text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 disabled:opacity-60"
                                        style={w.is_following
                                            ? { borderColor: "#1E1B4B", background: "#1E1B4B", color: "#D4AF37" }
                                            : { borderColor: "#D4AF37", color: "#1E1B4B" }}
                                        data-testid={`landing-warrior-follow-${w.id}`}
                                    >
                                        {w.is_following ? <><UserCheck size={12} /> Following</> : <><UserPlus size={12} /> Follow</>}
                                    </button>
                                )}
                            </div>
                        </article>
                    );
                })}
            </div>
        </section>
    );
}
